import { PlayerEvent, type PlayerInterface } from '@foundation-player/loader';
import { readonly, writable, type Readable, type Writable } from 'svelte/store';
import type { PlayerStoreModule } from './player-store-types';

export class StallingPlayerStore implements PlayerStoreModule<'stalling'> {
  #stallIndicatorTimeoutId?: ReturnType<typeof setTimeout>;
  readonly #stallIndicatorDelayInMs = 1000;
  readonly #stalling: Writable<boolean> = writable(false);
  readonly stalling: Readable<boolean> = readonly(this.#stalling);

  #player: PlayerInterface | undefined;

  initializeStateFromPlayer(player: PlayerInterface) {
    this.#player = player;

    player.addEventListener(PlayerEvent.Play, () => this.#setStalling());
    player.addEventListener(PlayerEvent.Paused, () => this.#unsetStalling());
    player.addEventListener(PlayerEvent.Seek, () => this.#onSeek());
    player.addEventListener(PlayerEvent.StallStarted, () => this.#setStalling());
    player.addEventListener(PlayerEvent.StallEnded, () => this.#unsetStalling());
    player.addEventListener(PlayerEvent.TimeChanged, () => this.#unsetStalling());
    player.addEventListener(PlayerEvent.Ready, () => this.#unsetStalling());
    player.addEventListener(PlayerEvent.SessionEnd, () => this.#unsetStalling());
  }

  #onSeek() {
    // TODO: This is a workaround and should be removed as soon as
    // https://rtltech.atlassian.net/browse/PLAYHTML5-402 is done.
    // Currently the player doesn't emit a StallStarted-event if the player
    // buffers/waits after a seek or play.
    if (this.#player?.isPlaying()) {
      this.#setStalling();
    }
  }

  #setStalling() {
    // We only want to have one timeout. Sometimes
    // the player sends multiple StallStarted events.
    // But if we restart the timer each time, we will
    // delay the spinner. This way the spinner
    // shows up as soon as possible.
    if (!this.#stallIndicatorTimeoutId) {
      this.#stallIndicatorTimeoutId = setTimeout(() => {
        this.#stalling.set(true);
        this.#stallIndicatorTimeoutId = undefined;
      }, this.#stallIndicatorDelayInMs);
    }
  }

  #unsetStalling() {
    this.#stalling.set(false);

    clearTimeout(this.#stallIndicatorTimeoutId);
    this.#stallIndicatorTimeoutId = undefined;
  }
}
